import { de } from "../../../website/src/data/de";
import { en } from "../../../website/src/data/en";
import { galleryImages } from "../../../website/src/data/site";
import { siteImages } from "../../../website/src/data/siteImages";
import type { FacilitySection, FacilityTab } from "../../../website/src/data/types";
import type { WebsiteBlocks, WebsiteContentDocument, WebsiteTile, WebsiteTileCopy } from "@/lib/websiteContentApi";

export type TileSectionId = "stories" | "highlights" | "facilities" | "surroundings" | "gallery" | "links";

export const TILE_SECTIONS: { id: TileSectionId; label: string; hint: string }[] = [
  {
    id: "stories",
    label: "Große Fotos",
    hint: "Bilder über die ganze Breite. Neue Fotos vom Platz kommen hier am besten zur Geltung.",
  },
  {
    id: "highlights",
    label: "Kacheln",
    hint: "Kurze Gründe, warum Gäste bleiben. Kacheln können Sie ergänzen, sortieren oder ausblenden.",
  },
  {
    id: "facilities",
    label: "Auf dem Platz",
    hint: "Sanitär, Shop, Gelände und alles, was Gäste auf dem Platz finden.",
  },
  {
    id: "surroundings",
    label: "Umgebung",
    hint: "Frankfurt, Einkauf, Main und andere Ausflüge.",
  },
  {
    id: "gallery",
    label: "Galerie",
    hint: "Weitere Fotos. Das erste Bild wird groß gezeigt, wenn Sie „Ganz groß“ wählen.",
  },
  {
    id: "links",
    label: "Sprungziele",
    hint: "Kacheln, die zu Buchung, Platzplan oder einer anderen Seite führen.",
  },
];

const LINK_CHOICES = [
  { value: "BOOKING", label: "Online-Buchung" },
  { value: "#platzplan", label: "Platzplan" },
  { value: "#galerie", label: "Galerie" },
  { value: "#unser-campingplatz", label: "Auf dem Platz" },
  { value: "#anfahrt", label: "Anfahrt" },
  { value: "/preise", label: "Preise" },
];

export function linkChoices(): { value: string; label: string }[] {
  return LINK_CHOICES;
}

function emptyCopy(): WebsiteTileCopy {
  return { eyebrow: "", title: "", text: "", detail: "", badge: "" };
}

function copy(partial: Partial<WebsiteTileCopy>): WebsiteTileCopy {
  return { ...emptyCopy(), ...partial };
}

export function blankTile(section: TileSectionId): WebsiteTile {
  return {
    id: crypto.randomUUID(),
    size: section === "stories" ? "large" : "normal",
    image: "",
    href: section === "links" ? "#galerie" : "",
    visible: true,
    highlights: [],
    de: copy({ title: "Neue Kachel" }),
    en: emptyCopy(),
  };
}

function overridden(doc: WebsiteContentDocument, lang: "de" | "en", key: string, fallback: string): string {
  const value = doc.texts?.[lang]?.[key];
  return value?.trim() ? value : fallback;
}

function imageOf(doc: WebsiteContentDocument, key: string, fallback: string): string {
  return doc.images?.[key]?.src || fallback;
}

function facilityDetail(tab: FacilityTab, english: FacilityTab | undefined, lang: "de" | "en"): string {
  const sections = lang === "en" && english ? english.sections : tab.sections;
  const includedLabel = lang === "de" ? "Enthalten" : "Included";
  const excludedLabel = lang === "de" ? "Nicht enthalten" : "Not included";
  return sections
    .map((section: FacilitySection) => {
      const parts = [`${section.title}\n${section.text}`];
      if (section.list?.included.length) {
        parts.push(`${includedLabel}:\n${section.list.included.map((item) => `• ${item}`).join("\n")}`);
      }
      if (section.list?.excluded.length) {
        parts.push(`${excludedLabel}:\n${section.list.excluded.map((item) => `• ${item}`).join("\n")}`);
      }
      return parts.join("\n\n");
    })
    .join("\n\n");
}

function seedStories(doc: WebsiteContentDocument): WebsiteTile[] {
  const moments = de.photoJourney.moments.map((moment, index) => {
    const english = en.photoJourney.moments[index];
    const key = `stories.moment-${index}`;
    return {
      id: `moment-${index}`,
      size: "large" as const,
      image: imageOf(doc, key, moment.image),
      href: "",
      visible: true,
      highlights: [],
      de: copy({
        eyebrow: overridden(doc, "de", `${key}.eyebrow`, moment.eyebrow),
        title: overridden(doc, "de", `${key}.title`, moment.title),
        text: overridden(doc, "de", `${key}.text`, moment.text),
      }),
      en: copy({
        eyebrow: overridden(doc, "en", `${key}.eyebrow`, english?.eyebrow ?? ""),
        title: overridden(doc, "en", `${key}.title`, english?.title ?? ""),
        text: overridden(doc, "en", `${key}.text`, english?.text ?? ""),
      }),
    };
  });
  const natureKey = "stories.nature";
  moments.push({
    id: "nature",
    size: "large",
    image: imageOf(doc, natureKey, siteImages.banners.nature.src),
    href: "",
    visible: true,
    highlights: [],
    de: copy({
      eyebrow: overridden(doc, "de", `${natureKey}.eyebrow`, de.imageBanners.nature.quote),
      title: overridden(doc, "de", `${natureKey}.title`, de.imageBanners.nature.title),
      text: overridden(doc, "de", `${natureKey}.text`, de.imageBanners.nature.subtitle),
    }),
    en: copy({
      eyebrow: overridden(doc, "en", `${natureKey}.eyebrow`, en.imageBanners.nature.quote),
      title: overridden(doc, "en", `${natureKey}.title`, en.imageBanners.nature.title),
      text: overridden(doc, "en", `${natureKey}.text`, en.imageBanners.nature.subtitle),
    }),
  });
  return moments;
}

function seedHighlights(doc: WebsiteContentDocument): WebsiteTile[] {
  return de.experience.items.map((item, index) => {
    const english = en.experience.items.find((entry) => entry.key === item.key);
    const image = siteImages.experience[item.key as keyof typeof siteImages.experience];
    return {
      id: `experience-${item.key}`,
      size: index === 0 ? "large" : "normal",
      image: imageOf(doc, `experience.${item.key}`, image.src),
      href: "",
      visible: true,
      highlights: [],
      de: copy({
        title: overridden(doc, "de", `experience.items.${item.key}.title`, item.title),
        text: overridden(doc, "de", `experience.items.${item.key}.description`, item.description),
      }),
      en: copy({
        title: overridden(doc, "en", `experience.items.${item.key}.title`, english?.title ?? ""),
        text: overridden(doc, "en", `experience.items.${item.key}.description`, english?.description ?? ""),
      }),
    };
  });
}

function seedFacilities(doc: WebsiteContentDocument): WebsiteTile[] {
  return de.facilities.tabs.map((tab) => {
    const english = en.facilities.tabs.find((entry) => entry.id === tab.id);
    const highlights = tab.highlights.map((item, index) =>
      overridden(doc, "de", `facilities.tabs.${tab.id}.highlights.${index}`, item),
    );
    return {
      id: `facility-${tab.id}`,
      size: "normal" as const,
      image: imageOf(doc, `facilities.${tab.id}`, tab.image),
      href: tab.sitePlan ? "#platzplan" : "",
      visible: true,
      highlights,
      de: copy({
        badge: overridden(doc, "de", `facilities.tabs.${tab.id}.label`, tab.label),
        title: overridden(doc, "de", `facilities.tabs.${tab.id}.heading`, tab.heading),
        text: overridden(doc, "de", `facilities.tabs.${tab.id}.summary`, tab.summary),
        detail: facilityDetail(tab, english, "de"),
      }),
      en: copy({
        badge: overridden(doc, "en", `facilities.tabs.${tab.id}.label`, english?.label ?? ""),
        title: overridden(doc, "en", `facilities.tabs.${tab.id}.heading`, english?.heading ?? ""),
        text: overridden(doc, "en", `facilities.tabs.${tab.id}.summary`, english?.summary ?? ""),
        detail: facilityDetail(tab, english, "en"),
      }),
    };
  });
}

function seedSurroundings(doc: WebsiteContentDocument): WebsiteTile[] {
  return de.surroundings.items.map((item, index) => {
    const english = en.surroundings.items.find((entry) => entry.key === item.key);
    const image = siteImages.surroundings[item.key as keyof typeof siteImages.surroundings];
    return {
      id: `surroundings-${item.key}`,
      size: index === 0 ? "large" : "normal",
      image: imageOf(doc, `surroundings.${item.key}`, image.src),
      href: "",
      visible: true,
      highlights: [],
      de: copy({
        title: overridden(doc, "de", `surroundings.items.${item.key}.name`, item.name),
        text: overridden(doc, "de", `surroundings.items.${item.key}.description`, item.description),
        badge: overridden(doc, "de", `surroundings.items.${item.key}.distance`, item.distance),
      }),
      en: copy({
        title: overridden(doc, "en", `surroundings.items.${item.key}.name`, english?.name ?? ""),
        text: overridden(doc, "en", `surroundings.items.${item.key}.description`, english?.description ?? ""),
        badge: overridden(doc, "en", `surroundings.items.${item.key}.distance`, english?.distance ?? ""),
      }),
    };
  });
}

function seedGallery(doc: WebsiteContentDocument): WebsiteTile[] {
  return galleryImages.map((image, index) => ({
    id: `gallery-${index}`,
    size: index === 0 ? "large" : "normal",
    image: imageOf(doc, `gallery.${index}`, image.src),
    href: "",
    visible: true,
    highlights: [],
    de: copy({ title: doc.images?.[`gallery.${index}`]?.alt?.de || image.alt }),
    en: copy({ title: doc.images?.[`gallery.${index}`]?.alt?.en || image.alt }),
  }));
}

function seedLinks(doc: WebsiteContentDocument): WebsiteTile[] {
  const items = [
    { id: "map", href: "#platzplan", de: "Platzplan", en: "Site map", image: siteImages.experience.meadow.src },
    { id: "booking", href: "BOOKING", de: de.nav.booking, en: en.nav.booking, image: siteImages.experience.evening.src },
    { id: "gallery", href: "#galerie", de: de.nav.gallery, en: en.nav.gallery, image: siteImages.experience.skyline.src },
    { id: "camp", href: "#unser-campingplatz", de: de.nav.campsite, en: en.nav.campsite, image: siteImages.experience.family.src },
  ];
  return items.map((item) => ({
    id: `link-${item.id}`,
    size: "normal" as const,
    image: imageOf(doc, `links.${item.id}`, item.image),
    href: item.href,
    visible: true,
    highlights: [],
    de: copy({ title: overridden(doc, "de", `quickLinks.${item.id}`, item.de) }),
    en: copy({ title: overridden(doc, "en", `quickLinks.${item.id}`, item.en) }),
  }));
}

export function seedSection(section: TileSectionId, doc: WebsiteContentDocument): WebsiteTile[] {
  if (section === "stories") return seedStories(doc);
  if (section === "highlights") return seedHighlights(doc);
  if (section === "facilities") return seedFacilities(doc);
  if (section === "surroundings") return seedSurroundings(doc);
  if (section === "gallery") return seedGallery(doc);
  return seedLinks(doc);
}

export function normalizeTile(raw: Partial<WebsiteTile> | null | undefined): WebsiteTile | null {
  if (!raw?.id) return null;
  const size = raw.size === "wide" || raw.size === "large" ? raw.size : "normal";
  return {
    id: raw.id,
    size,
    image: raw.image ?? "",
    href: raw.href ?? "",
    visible: raw.visible !== false,
    highlights: Array.isArray(raw.highlights) ? raw.highlights.filter((item) => item.trim()) : [],
    de: copy(raw.de ?? {}),
    en: copy(raw.en ?? {}),
  };
}

export function normalizeBlocks(raw: WebsiteBlocks | undefined): WebsiteBlocks {
  const blocks: WebsiteBlocks = {};
  for (const section of TILE_SECTIONS) {
    const items = raw?.[section.id];
    if (!Array.isArray(items)) continue;
    blocks[section.id] = items.map((item) => normalizeTile(item)).filter((item): item is WebsiteTile => item !== null);
  }
  return blocks;
}
